import type { Effort, Model } from '../agents/types';
import type { ModelProviderAdapter } from './ModelProviderAdapter';
import { MockAdapter } from './MockAdapter';
import { OpenAIAdapter } from './OpenAIAdapter';
import {
  claudeCliBackendAdapter,
  codexCliBackendAdapter,
  geminiCliBackendAdapter,
  hermesCliBackendAdapter,
  customCliBackendAdapter,
} from './BackendCliAdapter';
import { useUIStore } from '../store/uiStore';

interface ProviderInfo {
  id: string;
  label: string;
  configured: boolean;
  models: Model[];
  adapter?: ModelProviderAdapter;
  /** rendered separately from API providers */
  group?: 'cli' | 'api' | 'mock';
  /**
   * Effort levels this provider's CLI can actually act on. The Effort
   * picker re-renders against this list when the active provider
   * changes; an empty array hides the picker entirely (Gemini CLI has
   * no reasoning flag, so its list is empty).
   */
  effortLevels?: Effort[];
}

const stubModels = (providerId: string, items: Array<Omit<Model, 'providerId'>>): Model[] =>
  items.map((m) => ({ ...m, providerId }));

/**
 * Build the provider list against the *current* API key state.
 * Called fresh on each consumer render so toggling a key in Settings
 * flips a provider's `configured` and adapter without a reload.
 *
 * Order: terminal-CLI agents first (primary), then mock, then direct
 * API providers (advanced).
 */
export function getProviders(): ProviderInfo[] {
  const keys = useUIStore.getState().apiKeys;
  return [
    // ----- Terminal-CLI agents (run on the VPS via Loom backend) -----
    {
      id: 'claude_cli',
      label: 'Claude (CLI)',
      configured: true,
      group: 'cli',
      models: claudeCliBackendAdapter.listModels(),
      adapter: claudeCliBackendAdapter,
      effortLevels: ['low', 'medium', 'high', 'extra_high', 'max'],
    },
    {
      id: 'codex_cli',
      label: 'Codex (CLI)',
      configured: true,
      group: 'cli',
      models: codexCliBackendAdapter.listModels(),
      adapter: codexCliBackendAdapter,
      effortLevels: ['low', 'medium', 'high', 'extra_high'],
    },
    {
      id: 'gemini_cli',
      label: 'Gemini (CLI)',
      configured: true,
      group: 'cli',
      models: geminiCliBackendAdapter.listModels(),
      adapter: geminiCliBackendAdapter,
      effortLevels: [],
    },
    {
      id: 'hermes',
      label: 'Hermes',
      configured: false,
      group: 'cli',
      models: hermesCliBackendAdapter.listModels(),
      adapter: hermesCliBackendAdapter,
    },
    {
      id: 'custom_cli',
      label: 'Custom CLI',
      configured: false,
      group: 'cli',
      models: customCliBackendAdapter.listModels(),
      adapter: customCliBackendAdapter,
    },

    // ----- Mock (offline / dev) -----
    {
      id: 'mock',
      label: 'Mock',
      configured: true,
      group: 'mock',
      models: MockAdapter.listModels(),
      adapter: MockAdapter,
    },

    // ----- Direct API providers (advanced) -----
    {
      id: 'anthropic',
      label: 'Claude',
      group: 'api',
      configured: false, // CORS-blocked from browser; needs Tauri
      models: stubModels('anthropic', [
        { id: 'claude-opus-4-7', label: 'Opus 4.7' },
        { id: 'claude-opus-4-7-1m', label: 'Opus 4.7 M', badge: '1M' },
        { id: 'claude-sonnet-4-6', label: 'Sonnet 4.6' },
        { id: 'claude-haiku-4-5', label: 'Haiku 4.5' },
        { id: 'claude-opus-4-6', label: 'Opus 4.6', legacy: true },
      ]),
    },
    {
      id: 'openai',
      label: 'OpenAI',
      group: 'api',
      configured: !!keys.openai,
      models: OpenAIAdapter.listModels(),
      adapter: keys.openai ? OpenAIAdapter : undefined,
    },
    {
      id: 'gemini',
      label: 'Gemini',
      group: 'api',
      configured: false,
      models: stubModels('gemini', [
        { id: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
        { id: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
      ]),
    },
    {
      id: 'openrouter',
      label: 'OpenRouter',
      group: 'api',
      configured: false,
      models: stubModels('openrouter', [
        { id: 'openrouter/auto', label: 'Auto-route' },
      ]),
    },
    {
      id: 'local',
      label: 'Local',
      group: 'api',
      configured: false,
      models: stubModels('local', [
        { id: 'local-llama', label: 'Llama (local)' },
        { id: 'local-qwen', label: 'Qwen (local)' },
      ]),
    },
  ];
}

export function findModel(modelId: string): { provider: ProviderInfo; model: Model } | undefined {
  for (const provider of getProviders()) {
    const model = provider.models.find((m) => m.id === modelId);
    if (model) return { provider, model };
  }
  return undefined;
}

export function listAllModels(): Array<{ provider: ProviderInfo; model: Model }> {
  const out: Array<{ provider: ProviderInfo; model: Model }> = [];
  for (const provider of getProviders()) {
    for (const model of provider.models) {
      out.push({ provider, model });
    }
  }
  return out;
}

/**
 * Resolve which effort levels apply to the currently-selected model.
 * Falls back to the full Claude list if the provider has no explicit list
 * (so legacy/mock providers don't lose the picker).
 */
export function effortLevelsFor(modelId: string): Effort[] {
  const found = findModel(modelId);
  if (!found) return ['low', 'medium', 'high', 'extra_high', 'max'];
  return found.provider.effortLevels ?? ['low', 'medium', 'high', 'extra_high', 'max'];
}

